import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, OnInitEffects, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, exhaustMap, filter, map, of, tap, timeout } from 'rxjs';
import {
  canAccessBackoffice,
  extractBackofficeRoleFromToken,
  getDefaultBackofficeRoute,
  getDefaultBackofficeRouteForRoles,
  normalizeBackofficeRole,
} from '../../../core/auth/role-access.util';
import {
  extractPermissionsFromToken,
  normalizePermissions,
} from '../../../core/auth/auth-permissions.util';
import { AccessControlService } from '../../../core/services/access-control.service';
import { AuthSessionSyncService } from '../../../core/services/auth-session-sync.service';
import { AuthService } from '../providers/auth.service';
import { AuthActions } from './auth.actions';
import { authFeature } from './auth.feature';

/**
 * Upper bound for the GET /me/permissions round trip. On expiry the failure
 * effect takes over and routes the user with the legacy role claim.
 */
const ACCESS_PROFILE_TIMEOUT_MS = 10_000;

export class AuthEffects implements OnInitEffects {
  private readonly actions$ = inject(Actions);
  private readonly authService = inject(AuthService);
  private readonly accessControlService = inject(AccessControlService);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly sessionSync = inject(AuthSessionSyncService);

  readonly login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.login),
      exhaustMap(({ email, password }) =>
        this.authService.login({ email, password }).pipe(
          map((response) => {
            const role =
              normalizeBackofficeRole(response.role) ??
              extractBackofficeRoleFromToken(response.token) ??
              null;

            if (!role || !canAccessBackoffice(role)) {
              return AuthActions.loginFailure({
                error: 'No tienes permisos para acceder al backoffice.',
              });
            }

            return AuthActions.loginSuccess({
              token: response.token,
              refreshToken: response.refreshToken ?? null,
              userName: response.userName,
              role,
              publicId: response.publicId,
              permissions: [
                ...new Set([
                  ...normalizePermissions(response.permissions),
                  ...extractPermissionsFromToken(response.token),
                ]),
              ],
            });
          }),
          catchError((error) =>
            of(
              AuthActions.loginFailure({
                error: error?.error?.message ?? 'Error de autenticación',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly forgotPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.forgotPassword),
      exhaustMap((request) =>
        this.authService.forgotPassword(request).pipe(
          map((response) =>
            AuthActions.forgotPasswordSuccess({
              message:
                response.message ||
                'Si el correo existe en el sistema, recibiras las instrucciones en breve.',
            }),
          ),
          catchError((error) =>
            of(
              AuthActions.forgotPasswordFailure({
                error: this.getApiErrorMessage(error),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  // The landing route now depends on roles[], which only /me/permissions
  // knows, so this no longer navigates directly: it persists the legacy
  // session fields and hands off to loadAccessProfile$ for the real routing.
  readonly loginSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginSuccess),
      map(({ token, refreshToken, userName, role, publicId, permissions }) => {
        this.sessionSync.saveSession({
          token,
          refreshToken,
          userName,
          role,
          publicId,
          permissions,
        });
        return AuthActions.loadAccessProfile({ navigateOnComplete: true });
      }),
    ),
  );

  // A restored session must not trust stale storage: refresh permissions
  // from the endpoint instead of navigating (the user is already on a page).
  readonly initSuccessRefresh$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.initSuccess),
      filter((session) => !!session.token),
      map(() => AuthActions.loadAccessProfile({ navigateOnComplete: false })),
    ),
  );

  readonly loadAccessProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loadAccessProfile),
      exhaustMap(({ navigateOnComplete }) =>
        this.accessControlService.getAccessProfile().pipe(
          // Without this, a request that never settles leaves the user stuck
          // on the login screen: neither the success nor the failure effect
          // would ever run, and the failure path is what routes them onward.
          timeout(ACCESS_PROFILE_TIMEOUT_MS),
          map((profile) => AuthActions.loadAccessProfileSuccess({ profile, navigateOnComplete })),
          catchError((error) =>
            of(
              AuthActions.loadAccessProfileFailure({
                error: error?.error?.message ?? 'No fue posible cargar los permisos.',
                navigateOnComplete,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly loadAccessProfileSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loadAccessProfileSuccess),
        tap(({ profile, navigateOnComplete }) => {
          const session = this.sessionSync.readSession();

          // saveSession clears stored session first, so persisting without a
          // token would blank auth_token and force a logout on the next
          // request instead of refreshing permissions. Skip the write and let
          // the store hold the refreshed profile on its own.
          if (session.token) {
            this.sessionSync.saveSession({
              token: session.token,
              refreshToken: session.refreshToken,
              userName: session.userName ?? '',
              role: session.role ?? '',
              publicId: session.publicId ?? '',
              permissions: profile.permissions,
              userType: profile.userType,
              roles: profile.roles,
            });
          }

          if (navigateOnComplete) {
            this.router.navigate([getDefaultBackofficeRouteForRoles(profile.roles)]);
          }
        }),
      ),
    { dispatch: false },
  );

  // A failing endpoint must never strand the user on the login screen, so
  // this still navigates using the legacy role claim as a last resort.
  readonly loadAccessProfileFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loadAccessProfileFailure),
        tap(({ navigateOnComplete }) => {
          if (!navigateOnComplete) {
            return;
          }

          const legacyRole =
            this.store.selectSignal(authFeature.selectRole)() ??
            this.sessionSync.readSession().role;
          this.router.navigate([getDefaultBackofficeRoute(legacyRole)]);
        }),
      ),
    { dispatch: false },
  );

  readonly tokensRenewed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.tokensRenewed),
        tap(({ token, refreshToken }) => this.sessionSync.updateTokens(token, refreshToken)),
      ),
    { dispatch: false },
  );

  readonly logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logout),
        tap(() => {
          this.sessionSync.broadcastLogout();
          this.router.navigate(['/login']);
        }),
      ),
    { dispatch: false },
  );

  readonly sessionExpired$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.sessionExpired),
        tap(({ returnUrl }) => {
          this.sessionSync.broadcastLogout();
          this.router.navigate(['/login'], { queryParams: { returnUrl } });
        }),
      ),
    { dispatch: false },
  );

  ngrxOnInitEffects() {
    this.sessionSync.start();
    return AuthActions.init();
  }

  readonly init$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.init),
      map(() => AuthActions.initSuccess(this.sessionSync.readSession())),
    ),
  );

  private getApiErrorMessage(error: unknown): string {
    const apiError = error as { status?: number; error?: { message?: unknown } | string };
    const message =
      typeof apiError.error === 'object' && typeof apiError.error?.message === 'string'
        ? apiError.error.message
        : typeof apiError.error === 'string'
          ? apiError.error
          : null;

    return message
      ? `No fue posible enviar el correo: ${message}`
      : `No fue posible enviar el correo. Código HTTP: ${apiError.status || 'sin respuesta'}.`;
  }
}
